import { useState } from "react";
import { Avatar } from "@mui/material";
import { formatModelNumber } from "../helpers/statisticModelDisplay";
import StatisticModelAggregate from "./StatisticModelAggregate";
export default function StatisticModelTables({
  workspace,
  data,
  en,
  onSelect,
}) {
  const [page, setPage] = useState(0),
    [size, setSize] = useState(50);
  const t = (de, eng) => (en ? eng : de),
    fmt = (n, options) => formatModelNumber(n, en, options);
  const names = {},
    logos = {},
    fixtures = data?.fixtures || [];
  for (const f of fixtures) {
    logos[f.home_team_id] = f.home_image_path;
    logos[f.away_team_id] = f.away_image_path;
    names[f.home_team_id] = f.home_name || f.name?.split(" vs ")[0];
    names[f.away_team_id] = f.away_name || f.name?.split(" vs ")[1];
  }
  const records = new Map(
    (workspace?.records || []).map((r) => [r.pre.fixture_id, r])
  );
  const types = Object.keys(
    workspace?.records?.[0]?.pre?.expected_statistics || {}
  );
  const pair = (v, percent = false) =>
    v
      ? `${fmt(v.home * (percent ? 100 : 1))} / ${fmt(
          v.away * (percent ? 100 : 1)
        )}${percent ? " %" : ""}`
      : "—";
  return (
    <>
      <h3>{t("Statistikmodell-Ranking", "Statistics model ranking")}</h3>
      <div className="sm-grid">
        <table aria-label={t("Ranking", "Ranking")}>
          <thead>
            <tr>
              {[
                "#",
                "Team",
                t("Stärke (Tore / Gegentore)", "Strength (goals / conceded)"),
                t(
                  "Torerwartung gegen Liga-Durchschnitt",
                  "Expected goals against league average"
                ),
                t(
                  "Gegentorerwartung gegen Liga-Durchschnitt",
                  "Expected goals conceded against league average"
                ),
                t("Erwartete Torsumme", "Expected goal sum"),
                "Conversion For / Against",
              ].map((x) => (
                <th key={x}>{x}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(workspace?.ranking || []).map((r) => (
              <tr key={r.team_id}>
                <td>{r.rank}</td>
                <th className="sm-sticky-team">
                  <span className="sm-team">
                    <Avatar
                      src={logos[r.team_id]}
                      alt={names[r.team_id]}
                      sx={{ width: 24, height: 24 }}
                    >
                      {names[r.team_id]?.[0]}
                    </Avatar>
                    {names[r.team_id] || r.team_id}
                  </span>
                </th>
                <td>{fmt(r.total_strength)}</td>
                <td>{fmt(r.for_total)}</td>
                <td>{fmt(r.against_total)}</td>
                <td>{fmt(r.total_goals)}</td>
                <td>
                  {fmt(r.conversion_for, { percent: true })} /{" "}
                  {fmt(r.conversion_against, { percent: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3>
        {t(
          "Fixtures · vollständige Expected Statistics",
          "Fixtures · complete expected statistics"
        )}
      </h3>
      <p>
        {t(
          "H / A je Statistik. Ballbesitz in %. — bedeutet noch nicht berechnet oder historischer Parameterstand fehlt. Fixture anklicken für Actual, Error und Should.",
          "H / A per statistic. Possession in %. — means not calculated or historical parameters unavailable. Select a fixture for actuals, errors and Should."
        )}
      </p>
      <div className="sm-controls">
        <label>
          {t("Zeilen", "Rows")}{" "}
          <select
            aria-label={t("Zeilen", "Rows")}
            value={size}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
          >
            {[50, 100, 500].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </label>
        <button disabled={!page} onClick={() => setPage(page - 1)}>
          {t("Zurück", "Previous")}
        </button>
        <span>
          {page + 1} / {Math.max(1, Math.ceil(fixtures.length / size))}
        </span>
        <button
          disabled={(page + 1) * size >= fixtures.length}
          onClick={() => setPage(page + 1)}
        >
          {t("Weiter", "Next")}
        </button>
      </div>
      <div className="sm-grid sm-fixtures">
        <table aria-label="Expected Statistics">
          <thead>
            <tr>
              <th>Details</th>
              <th>{t("Datum", "Date")}</th>
              <th>{t("Heim", "Home")}</th>
              <th>{t("Auswärts", "Away")}</th>
              {types.map((id) => (
                <th key={id}>
                  {workspace.records[0].pre.expected_statistics[id].title} H / A
                </th>
              ))}
              {[
                "Attack Moments H / A",
                "Conversion H / A",
                "xG H / A",
                "1 / X / 2 %",
                "Fair Odds 1 / X / 2",
              ].map((x) => (
                <th key={x}>{x}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fixtures.slice(page * size, (page + 1) * size).map((f) => {
              const r = records.get(f.id),
                p = r?.pre;
              return (
                <tr key={f.id}>
                  <td>
                    <button disabled={!r} onClick={() => onSelect(r)}>
                      {t("Öffnen", "Open")}
                    </button>
                    <small title="Fixture ID"> {f.id}</small>
                  </td>
                  <td>{f.kickoff.slice(0, 10)}</td>
                  <th className="sm-sticky-team">
                    <span className="sm-team">
                      <Avatar
                        src={logos[f.home_team_id]}
                        alt={names[f.home_team_id]}
                        sx={{ width: 22, height: 22 }}
                      >
                        {names[f.home_team_id]?.[0]}
                      </Avatar>
                      {names[f.home_team_id]}
                    </span>
                  </th>
                  <th>
                    <span className="sm-team">
                      <Avatar
                        src={logos[f.away_team_id]}
                        alt={names[f.away_team_id]}
                        sx={{ width: 22, height: 22 }}
                      >
                        {names[f.away_team_id]?.[0]}
                      </Avatar>
                      {names[f.away_team_id]}
                    </span>
                  </th>
                  {types.map((id) => (
                    <td key={id}>
                      {pair(p?.expected_statistics[id], id === "45")}
                    </td>
                  ))}
                  <td>{pair(p?.expected_attack_moments)}</td>
                  <td>{pair(p?.expected_conversion, true)}</td>
                  <td>{pair(p?.statistic_xg)}</td>
                  <td>
                    {["homewinprob", "drawprob", "awaywinprob"]
                      .map((k) => fmt(p?.probabilities?.[k] * 100))
                      .join(" / ")}
                  </td>
                  <td>
                    {["homewinprob", "drawprob", "awaywinprob"]
                      .map((k) => fmt(p?.fair_odds?.[k]))
                      .join(" / ")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <StatisticModelAggregate records={workspace?.records} en={en} />
    </>
  );
}
